import {
  FeatureAction,
  FeatureContextInput,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionQuery
} from "@pathwright/graphql-client/src/generated"
import useCohortPermissions from "../cohort/useCohortPermissions"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canAdministrate } from "../user/permissions"

// ActionPermissionMap matches a feature action to either another
// feature action that it depends on or a feature permission
type ActionPermissionMap = Partial<{
  [key in FeatureAction]: {
    featureAction?: FeatureAction
    featurePermission?: FeaturePermission
    contexts: (null | { resource_id?: null; cohort_id?: null })[]
  }
}>

const CFP_UPDATE_PERMISSIONS: ActionPermissionMap = {
  [FeatureAction.View]: {
    featureAction: FeatureAction.UpdateVisibility,
    contexts: [
      null,
      { resource_id: null },
      { resource_id: null, cohort_id: null }
    ]
  },
  [FeatureAction.UpdateVisibility]: {
    featurePermission: FeaturePermission.AdminOnly,
    contexts: [null]
  },
  [FeatureAction.AuthLogin]: {
    featurePermission: FeaturePermission.AdminOnly,
    contexts: [null]
  }
  // TODO: implement home screen permissions
  // [FeatureAction.HomeScreenView]: {
  //   featurePermission: FeaturePermission.AdminOnly,
  //   contexts: [null]
  // }
}

const CFP_VIEW_PERMISSIONS: ActionPermissionMap = {
  [FeatureAction.View]: {
    featureAction: FeatureAction.View,
    contexts: [{ resource_id: null }, { resource_id: null, cohort_id: null }]
  },
  [FeatureAction.UpdateVisibility]: {
    featurePermission: FeaturePermission.AdminOnly,
    contexts: [null]
  }
}

type BaseFeaturePermissionProps = {
  featureKey: FeatureKey
  featureAction: FeatureAction
  context: FeatureContextInput | null
}

type UseFeaturePermissionProps = {
  actionPermissionMap: ActionPermissionMap
} & BaseFeaturePermissionProps

// returns { featurePermission: FeaturePermission, hasPermission: Boolean }
const useFeaturePermission = ({
  featureKey,
  featureAction,
  context,
  actionPermissionMap
}: UseFeaturePermissionProps) => {
  const pwContext = usePathwrightContext()
  // get the action that this feature depends on

  const dependentPermission = actionPermissionMap[featureAction]
  const {
    featureAction: dependentFeatureAction,
    featurePermission: dependentFeaturePermission,
    contexts: permissableContexts
  } = dependentPermission || {
    featureAction: null,
    featurePermission: null,
    contexts: []
  }

  // optionally query for the permission on the dependent feature
  const cfpPermissionQuery = useContextualFeaturePermissionQuery({
    variables: {
      featureKey,
      featureAction: dependentFeatureAction!,
      context
    },
    skip: !dependentFeatureAction
  })

  const permissionInContext =
    cfpPermissionQuery.data?.contextualFeaturePermission?.permission_in_context

  const cohortPerms = useCohortPermissions({
    cohortId: context && context.cohort_id
  })

  // get the applicable feature permission
  let featurePermission =
    dependentFeaturePermission || permissionInContext || null
  let hasPermission: boolean | null = null

  if (featurePermission) {
    // NOTE: no break statements means first matching case will fall through to the rest
    // hence ordering of broadest permission to narrowest
    switch (featurePermission) {
      // @ts-ignore
      case FeaturePermission.Everyone:
        if (cohortPerms.hasObserverLevelAccess) hasPermission = true
      // @ts-ignore: fallthrough
      case FeaturePermission.StaffOnly:
        if (cohortPerms.hasModeratorLevelAccess) hasPermission = true
        if (cohortPerms.hasEditorLevelAccess) hasPermission = true
      // @ts-ignore: fallthrough
      case FeaturePermission.AdminOnly:
      case FeaturePermission.Disabled:
        if (canAdministrate(pwContext)) hasPermission = true
    }

    // ensure permission can be updated for the given context
    const canUpdateForContext =
      permissableContexts.findIndex(
        (permissableContext) =>
          // both options are nullish
          context == permissableContext ||
          // both options are object, and share same keys
          (context &&
            permissableContext &&
            Object.keys(context).every(
              (key) => Object.keys(permissableContext).indexOf(key) > -1
            ))
      ) > -1

    hasPermission = hasPermission && canUpdateForContext

    if (!hasPermission) {
      // permission check is complete, user does not have permission
      hasPermission = false
    }
  }

  // returning null can be used as an indication that the permission check
  // is still loading and the permission is yet to be determined
  return { featurePermission, hasPermission }
}

export const useFeatureUpdatePermission = ({
  featureKey,
  featureAction,
  context
}: BaseFeaturePermissionProps) =>
  useFeaturePermission({
    featureKey,
    featureAction,
    context,
    actionPermissionMap: CFP_UPDATE_PERMISSIONS
  })

export const useFeatureViewPermission = ({
  featureKey,
  featureAction,
  context
}: BaseFeaturePermissionProps) =>
  useFeaturePermission({
    featureKey,
    featureAction,
    context,
    actionPermissionMap: CFP_VIEW_PERMISSIONS
  })
